import React from 'react';
import { SystemConfig } from '@/types';
import { CreditCard, FileText, Loader2 } from 'lucide-react';
import { formatTaxRateMappingsText, parseTaxRateMappingsText } from './settingsShared';

type OpayoMode = SystemConfig['opayo']['mode'];
const isOpayoMode = (value: string): value is OpayoMode => value === 'test' || value === 'live';

interface PaymentsSectionProps {
  config: SystemConfig;
  onUpdate: (config: SystemConfig) => void;
  sageTaxMappingsText: string;
  onSageTaxMappingsTextChange: (text: string) => void;
  isConnectingSage: boolean;
  onConnectSageOauth: () => void;
}

export const PaymentsSection: React.FC<PaymentsSectionProps> = ({
  config,
  onUpdate,
  sageTaxMappingsText,
  onSageTaxMappingsTextChange,
  isConnectingSage,
  onConnectSageOauth
}) => (
  <div className="space-y-8 animate-fade-in">
    <div className="bg-white p-8 rounded-2xl border border-slate-200 shadow-sm">
      <div className="flex justify-between items-center mb-6 border-b pb-4">
        <h4 className="font-bold text-lg text-slate-800 flex items-center gap-2">
          <CreditCard className="w-5 h-5 text-indigo-600" /> Opayo Pi Integration
        </h4>
        <label className="flex items-center gap-3 cursor-pointer">
          <span className="text-xs font-bold text-slate-500 uppercase">Integration Active</span>
          <input
            type="checkbox"
            checked={config.opayo.enabled}
            onChange={(e) =>
              onUpdate({ ...config, opayo: { ...config.opayo, enabled: e.target.checked } })
            }
            className="w-6 h-6 text-teal-600 rounded-lg border-slate-300"
          />
        </label>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <div>
          <label className="block text-xs font-bold text-slate-500 uppercase mb-2">
            Vendor Name
          </label>
          <input
            className="w-full border rounded-xl p-3 bg-slate-50 font-mono text-sm"
            value={config.opayo.vendorName}
            onChange={(e) =>
              onUpdate({ ...config, opayo: { ...config.opayo, vendorName: e.target.value } })
            }
          />
        </div>
        <div>
          <label className="block text-xs font-bold text-slate-500 uppercase mb-2">
            Environment
          </label>
          <select
            className="w-full border rounded-xl p-3 bg-slate-50 text-sm font-bold"
            value={config.opayo.mode}
            onChange={(e) => {
              if (isOpayoMode(e.target.value)) {
                onUpdate({ ...config, opayo: { ...config.opayo, mode: e.target.value } });
              }
            }}
          >
            <option value="test">Sandbox (Development)</option>
            <option value="live">Production (Live)</option>
          </select>
        </div>
      </div>
      <div className="space-y-6">
        <div>
          <label className="block text-xs font-bold text-slate-500 uppercase mb-2">
            Integration Key
          </label>
          <input
            type="password"
            placeholder="••••••••••••••••"
            className="w-full border rounded-xl p-3 bg-slate-50 font-mono text-sm"
            value={config.opayo.integrationKey}
            onChange={(e) =>
              onUpdate({ ...config, opayo: { ...config.opayo, integrationKey: e.target.value } })
            }
          />
        </div>
        <div>
          <label className="block text-xs font-bold text-slate-500 uppercase mb-2">
            Integration Password
          </label>
          <input
            type="password"
            placeholder="••••••••••••••••"
            className="w-full border rounded-xl p-3 bg-slate-50 font-mono text-sm"
            value={config.opayo.integrationPassword}
            onChange={(e) =>
              onUpdate({
                ...config,
                opayo: { ...config.opayo, integrationPassword: e.target.value }
              })
            }
          />
        </div>
      </div>
    </div>

    <div className="bg-white p-8 rounded-2xl border border-slate-200 shadow-sm">
      <div className="flex justify-between items-center mb-6 border-b pb-4">
        <h4 className="font-bold text-lg text-slate-800 flex items-center gap-2">
          <FileText className="w-5 h-5 text-teal-600" /> Sage Accounting (Draft Invoices)
        </h4>
        <label className="flex items-center gap-3 cursor-pointer">
          <span className="text-xs font-bold text-slate-500 uppercase">Sync Enabled</span>
          <input
            type="checkbox"
            checked={config.sage.enabled}
            onChange={(e) =>
              onUpdate({ ...config, sage: { ...config.sage, enabled: e.target.checked } })
            }
            className="w-6 h-6 text-teal-600 rounded-lg border-slate-300"
          />
        </label>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <div>
          <label className="block text-xs font-bold text-slate-500 uppercase mb-2">
            API Base URL
          </label>
          <input
            className="w-full border rounded-xl p-3 bg-slate-50 font-mono text-sm"
            value={config.sage.apiBaseUrl}
            onChange={(e) =>
              onUpdate({ ...config, sage: { ...config.sage, apiBaseUrl: e.target.value } })
            }
          />
        </div>
        <div>
          <label className="block text-xs font-bold text-slate-500 uppercase mb-2">
            Business ID
          </label>
          <input
            className="w-full border rounded-xl p-3 bg-slate-50 font-mono text-sm"
            value={config.sage.businessId}
            onChange={(e) =>
              onUpdate({ ...config, sage: { ...config.sage, businessId: e.target.value } })
            }
          />
        </div>
        <div className="md:col-span-2">
          <label className="block text-xs font-bold text-slate-500 uppercase mb-2">
            Client Credentials
          </label>
          <div className="border rounded-xl p-3 bg-slate-100 text-xs text-slate-700">
            Client ID and Client Secret are managed via server env variables:{' '}
            <code> SAGE_CLIENT_ID </code> and <code> SAGE_CLIENT_SECRET</code>.
          </div>
        </div>
        <div className="md:col-span-2">
          <label className="block text-xs font-bold text-slate-500 uppercase mb-2">
            OAuth Connection
          </label>
          <div className="border rounded-xl p-4 bg-slate-50 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div>
              <p className="text-xs font-bold text-slate-700">
                {config.sage.refreshToken
                  ? 'Sage is connected (refresh token saved).'
                  : 'Sage is not connected yet.'}
              </p>
              <p className="text-[11px] text-slate-500 mt-1">
                Uses OAuth and stores the refresh token securely after successful authorization.
              </p>
            </div>
            <button
              type="button"
              onClick={onConnectSageOauth}
              disabled={isConnectingSage}
              className="px-4 py-2 rounded-xl bg-teal-600 text-white text-xs font-black uppercase tracking-widest hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isConnectingSage ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 animate-spin" /> Connecting...
                </>
              ) : (
                'Connect Sage OAuth'
              )}
            </button>
          </div>
        </div>
        <div>
          <label className="block text-xs font-bold text-slate-500 uppercase mb-2">
            Default Ledger Account ID
          </label>
          <input
            className="w-full border rounded-xl p-3 bg-slate-50 font-mono text-sm"
            value={config.sage.defaultLedgerAccountId}
            onChange={(e) =>
              onUpdate({
                ...config,
                sage: { ...config.sage, defaultLedgerAccountId: e.target.value }
              })
            }
          />
        </div>
        <div>
          <label className="block text-xs font-bold text-slate-500 uppercase mb-2">
            Default Due Days
          </label>
          <input
            type="number"
            min={1}
            max={365}
            className="w-full border rounded-xl p-3 bg-slate-50 font-mono text-sm"
            value={config.sage.defaultDueDays}
            onChange={(e) =>
              onUpdate({
                ...config,
                sage: { ...config.sage, defaultDueDays: Math.max(1, Number(e.target.value) || 30) }
              })
            }
          />
        </div>
      </div>
      <div>
        <label className="block text-xs font-bold text-slate-500 uppercase mb-2">
          VAT % to Sage Tax Rate Mapping
        </label>
        <textarea
          rows={5}
          className="w-full border rounded-xl p-3 bg-slate-50 outline-none focus:ring-2 focus:ring-teal-500 text-sm font-mono"
          placeholder={'20=STANDARD_TAX_RATE_ID\n0=ZERO_TAX_RATE_ID'}
          value={sageTaxMappingsText}
          onChange={(e) => onSageTaxMappingsTextChange(e.target.value)}
          onBlur={() =>
            onUpdate({
              ...config,
              sage: {
                ...config.sage,
                taxRateMappings: parseTaxRateMappingsText(sageTaxMappingsText)
              }
            })
          }
        />
        <p className="text-[11px] text-slate-500 mt-2">
          One mapping per line in the format <code>VAT%=SAGE_TAX_RATE_ID</code>. Unmapped VAT values
          fail only those specific booking items.
        </p>
      </div>
    </div>
  </div>
);
